import moment from 'moment-timezone';
import { isNull } from 'lodash';
import { visitInfo } from 'utils/patientDetailsVisitInfo';
import { DATE_FORMATS, formatDateWithTimezone } from 'utils/dateTime';
import { getLessonsPerWeek } from './tableUtils';
import { normalizeDaysSinceClinicVisit } from '../utils';

const DEFAULT_EMPTY_VALUE = '-';

export const formatDate = (
  date: Nullable<string>,
  timezone: string | null,
): string => {
  if (!date) return DEFAULT_EMPTY_VALUE;
  return formatDateWithTimezone(date, timezone, DATE_FORMATS.M_D_YYYY);
};

export const formatBirthDate = (birthDate: Nullable<string>): string =>
  birthDate
    ? moment
        .utc(
          birthDate,
          [DATE_FORMATS.YYYY_MM_DD, DATE_FORMATS.UTC_DATE_TIME],
          true,
        )
        .format(DATE_FORMATS.M_D_YYYY)
    : DEFAULT_EMPTY_VALUE;

export const formatDayRemainings = (
  dayRemainings: Nullable<number>,
): string | number => {
  if (!isNull(dayRemainings) && Number.isInteger(dayRemainings)) {
    return dayRemainings >= 0 ? dayRemainings : DEFAULT_EMPTY_VALUE;
  }
  return DEFAULT_EMPTY_VALUE;
};

export const formatPrimaryClinician = (
  primaryClinician: Nullable<string>,
): string => primaryClinician || DEFAULT_EMPTY_VALUE;

const getKey = (patientId: string, clinicId: string): string =>
  `${patientId}-${clinicId}`;

const formatPhiDisclosure = (phiDisclosure: Nullable<boolean>) => {
  if (phiDisclosure == null) return DEFAULT_EMPTY_VALUE;
  return phiDisclosure ? 'Yes' : 'No';
};

export const formatTosPhiDisclosure = (
  phiDisclosure: Nullable<boolean>,
  tosVersionAccepted: Nullable<string>,
) => {
  const tosValue = tosVersionAccepted ? 'Yes' : DEFAULT_EMPTY_VALUE;
  let phiValue = formatPhiDisclosure(phiDisclosure);
  return `${tosValue} / ${phiValue}`;
};

export const formatData = (
  patientAdherenceDetailsList: IFormattedPatientAdherenceDetails[],
): IFormattedPatientType[] => {
  const patients = patientAdherenceDetailsList.map(
    (patient: IFormattedPatientAdherenceDetails) => {
      return {
        key: getKey(patient.id, patient.clinicId),
        firstName: patient.firstName,
        lastName: patient.lastName,
        birthDate: patient.birthDate,
        email: patient.email,
        clinicName: patient.clinicName,
        healthCareProvider: patient.primaryHealthCareProvider,
        externalRef: patient.externalRef || null,
        accessCode: patient.accessCode,
        rxCreatedDate: patient.rxCreatedDate,
        rxStartDate: patient.rxStartDate,
        rxDaysRemaining: patient.rxDaysRemaining,
        lessonsPerWeek: getLessonsPerWeek(patient),
        totalLessonsCompleted: patient.totalLessonsCompleted,
        daysSinceClinicVisit: normalizeDaysSinceClinicVisit(
          patient.daysSinceClinicVisit,
        ),
        rewardsEarnedLast7Days: patient.inAppCm
          ? patient.rewardsEarnedLast7Days
          : null,
        rewardsEarnedTotal: patient.inAppCm ? patient.rewardsEarnedTotal : null,
        rxTimezone: patient.rxTimezone,
        lastClinicVisit: patient.lastClinicVisit,
        visitInfo: visitInfo(
          patient.rxDaysRemaining,
          patient.daysSinceClinicVisit,
          patient.rxStartDate,
        ),
        phiDisclosure: patient.phiDisclosure,
        tosVersionAccepted: patient.tosVersionAccepted,
      };
    },
  );
  return patients;
};
